import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, BarChart2, ChevronRight, ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  plan: string;
  createdAt: string;
  onboardingCompleted: boolean;
  trialEndsAt?: string;
  _count: { listings: number; jobs: number };
}

interface AdminStats {
  totalUsers: number;
  totalListings: number;
  totalSales: number;
  byPlan: Record<string, number>;
}

interface UserDetail extends AdminUser {
  platformAccounts: { platform: string; connected: boolean; username?: string }[];
  listings: { id: string; title: string; price: number; status: string; createdAt: string }[];
}

const PLAN_COLOURS: Record<string, string> = {
  trial: 'text-white/40 bg-white/[0.05]',
  starter: 'text-primary-300 bg-primary-500/10',
  pro: 'text-secondary-400 bg-secondary-400/10',
};

export function AdminPage() {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const { data: stats } = useQuery<AdminStats>({
    queryKey: ['admin-stats'],
    queryFn: () => api.get('/admin/stats').then((r) => r.data),
  });

  const { data: users = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/admin/users').then((r) => r.data),
  });

  if (selectedUser) {
    return <UserDetailView id={selectedUser} onBack={() => setSelectedUser(null)} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-base font-semibold tracking-tight flex items-center gap-2">
          <Users size={16} className="text-primary-400" /> Admin panel
        </h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">Only visible to you</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-2.5">
          <StatCard label="Total users" value={stats.totalUsers} colour="#7c5cfc" />
          <StatCard label="Total listings" value={stats.totalListings} colour="#2de8c8" />
          <StatCard label="Total sales" value={stats.totalSales} colour="#4ade80" />
          <StatCard label="Pro accounts" value={stats.byPlan.pro ?? 0} colour="#a78bfa" />
        </div>
      )}

      {/* Plan breakdown */}
      {stats && (
        <div className="card p-4 flex items-center gap-6">
          <span className="text-[11px] text-white/30 font-medium">Plans</span>
          {Object.entries(stats.byPlan).map(([plan, count]) => (
            <div key={plan} className="flex items-center gap-1.5">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded capitalize ${PLAN_COLOURS[plan] ?? 'text-white/40 bg-white/[0.05]'}`}>{plan}</span>
              <span className="text-[11px] text-white/50">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Users table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.05]">
          <h2 className="text-[13px] font-semibold">All accounts</h2>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-white/30 text-sm">Loading...</div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => setSelectedUser(user.id)}
                className="w-full flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-400 flex items-center justify-center text-xs font-semibold text-white flex-shrink-0">
                  {user.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-white/85">{user.name}</div>
                  <div className="text-[11px] text-white/35">{user.email}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded capitalize ${PLAN_COLOURS[user.plan] ?? ''}`}>{user.plan}</span>
                  <span className="text-[11px] text-white/30">{user._count.listings} listings</span>
                  <span className="text-[10px] text-white/20">{new Date(user.createdAt).toLocaleDateString('en-GB')}</span>
                  <ChevronRight size={14} className="text-white/20" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserDetailView({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();

  const { data: user, isLoading } = useQuery<UserDetail>({
    queryKey: ['admin-user', id],
    queryFn: () => api.get(`/admin/users/${id}`).then((r) => r.data),
  });

  const updatePlan = useMutation({
    mutationFn: (plan: string) => api.patch(`/admin/users/${id}`, { plan }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user', id] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
    },
  });

  if (isLoading || !user) return <div className="p-8 text-center text-white/30">Loading...</div>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="btn-secondary !py-1 !px-2.5 flex items-center gap-1 text-[11px]">
          <ArrowLeft size={12} /> Back
        </button>
        <h1 className="text-base font-semibold">{user.name}</h1>
        <span className="text-[11px] text-white/35">{user.email}</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Account info */}
        <div className="card p-4 space-y-3">
          <h2 className="text-[13px] font-semibold">Account</h2>
          <Row label="Email" value={user.email} />
          <Row label="Joined" value={new Date(user.createdAt).toLocaleDateString('en-GB')} />
          <Row label="Onboarded" value={user.onboardingCompleted ? 'Yes' : 'No'} />
          <Row label="Listings" value={String(user._count.listings)} />
          <Row label="Jobs" value={String(user._count.jobs)} />

          <div>
            <div className="text-[10px] text-white/35 mb-1.5">Plan</div>
            <div className="flex gap-1.5">
              {['trial', 'starter', 'pro'].map((plan) => (
                <button
                  key={plan}
                  onClick={() => updatePlan.mutate(plan)}
                  disabled={updatePlan.isPending}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border capitalize transition-colors ${
                    user.plan === plan
                      ? 'border-primary-500/40 bg-primary-500/10 text-primary-300'
                      : 'border-white/[0.07] text-white/35 hover:bg-white/[0.03]'
                  }`}
                >
                  {plan}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Connected platforms */}
        <div className="card p-4">
          <h2 className="text-[13px] font-semibold mb-3">Platforms</h2>
          {user.platformAccounts.length === 0 ? (
            <p className="text-[11px] text-white/30">No platforms connected</p>
          ) : (
            <div className="space-y-1.5">
              {user.platformAccounts.map((p) => (
                <div key={p.platform} className="flex items-center justify-between text-[12px]">
                  <span className="capitalize text-white/60">{p.platform}</span>
                  <span className={p.connected ? 'text-success text-[11px]' : 'text-white/25 text-[11px]'}>
                    {p.connected ? `Connected${p.username ? ` · ${p.username}` : ''}` : 'Not connected'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent listings */}
      {user.listings.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/[0.05]">
            <h2 className="text-[13px] font-semibold">Recent listings ({user._count.listings} total)</h2>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {user.listings.map((listing) => (
              <div key={listing.id} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[12px] text-white/70 truncate flex-1">{listing.title}</span>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <span className="text-[12px] text-secondary-400">£{(listing.price / 100).toFixed(2)}</span>
                  <span className="text-[10px] text-white/30 capitalize">{listing.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, colour }: { label: string; value: number; colour: string }) {
  return (
    <div className="card p-3.5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: colour }} />
      <div className="text-[10.5px] text-white/30 mb-1">{label}</div>
      <div className="text-[24px] font-semibold tracking-tight" style={{ color: colour }}>{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-white/35">{label}</span>
      <span className="text-white/70">{value}</span>
    </div>
  );
}
